import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExecutiveSummaryHero = ({ onRoleSelect }) => {
  const [selectedRole, setSelectedRole] = useState(null);

  const keyMetrics = [
    {
      value: '90 Days',
      label: 'Launch Timeline',
      subtext: 'From decision to operational',
      icon: 'Zap',
      color: 'text-blue-600'
    },
    {
      value: '40-60%',
      label: 'Cost Savings',
      subtext: 'vs US operations',
      icon: 'TrendingDown',
      color: 'text-green-600'
    },
    {
      value: 'Guaranteed',
      label: 'Compliance',
      subtext: 'Milestone-based legal & regulatory assurance',
      icon: 'Shield',
      color: 'text-purple-600'
    },
    {
      value: '1,000+',
      label: 'Professionals Placed',
      subtext: 'Across engagements since 2018',
      icon: 'Users',
      color: 'text-orange-600'
    }
  ];

  const executiveRoles = [
    {
      role: 'CEO',
      icon: 'Users',
      focus: 'Strategic Impact & Competitive Advantage',
      keyQuestions: ['Time to market?', 'Strategic value?', 'Risk mitigation?']
    },
    {
      role: 'CFO',
      icon: 'DollarSign',
      focus: 'Total Cost & ROI Predictability',
      keyQuestions: ['True all-in cost?', 'ROI timeline?', 'Hidden costs?']
    },
    {
      role: 'CIO',
      icon: 'Code',
      focus: 'Technology Excellence & Integration',
      keyQuestions: ['Security posture?', 'Tech stack?', 'Integration complexity?']
    }
  ];

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (onRoleSelect) {
      onRoleSelect(role);
    }
  };

  return (
    <section className="relative bg-background text-text-primary py-24 lg:py-32 overflow-hidden border-b border-border">
      {/* Subtle background accent */}
      <div className="absolute inset-0 bg-muted/30"></div>

      <div className="container-executive relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Main Hero Content */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="Sparkles" size={16} />
              <span>Executive Edition</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-headline font-bold mb-6 leading-tight text-text-primary">
              Fortune 500-Grade GCC in{' '}
              <span className="text-accent">90 Days</span>
              <span className="block mt-2 text-3xl lg:text-4xl">Not 18 Months</span>
            </h1>

            <p className="text-xl lg:text-2xl text-text-secondary max-w-4xl mx-auto mb-8 leading-relaxed">
              Eliminate the complexity, uncertainty, and risk of establishing your Global Capability Center in India. 
              <span className="block mt-2 font-semibold text-text-primary">Board-ready business case in 10 days. Operational excellence in 90.</span>
            </p>

            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a href="#transparent-pricing">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calculator"
                  iconPosition="left"
                >
                  Calculate Your ROI
                </Button>
              </a>
              <a href="#transparent-pricing">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="FileText"
                  iconPosition="left"
                >
                  See All-In Pricing
                </Button>
              </a>
              <a href="#risk-mitigation">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Shield"
                  iconPosition="left"
                >
                  Risk Assessment
                </Button>
              </a>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {keyMetrics.map((metric, index) => (
              <div
                key={index}
                className="card-elevated rounded-xl p-6 hover:shadow-executive-hover transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-3">
                  <Icon name={metric.icon} size={24} className={metric.color} />
                </div>
                <div className="text-3xl font-bold mb-1 text-text-primary">{metric.value}</div>
                <div className="text-sm font-medium text-text-primary mb-1">{metric.label}</div>
                <div className="text-xs text-text-secondary">{metric.subtext}</div>
              </div>
            ))}
          </div>

          {/* Role-Based Navigation */}
          <div className="card-elevated rounded-2xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl lg:text-3xl font-headline font-bold mb-3 text-text-primary">
                View Through Your Leadership Lens
              </h2>
              <p className="text-text-secondary text-lg">
                Select your role for tailored insights and decision frameworks
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {executiveRoles.map((exec) => (
                <button
                  key={exec.role}
                  onClick={() => handleRoleSelect(exec.role)}
                  className={`bg-card border-2 rounded-xl p-6 text-left transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg ${
                    selectedRole === exec.role 
                      ? 'border-primary ring-2 ring-primary/20 shadow-lg' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      selectedRole === exec.role ? 'bg-primary' : 'bg-primary/10'
                    }`}>
                      <Icon 
                        name={exec.icon} 
                        size={24} 
                        color={selectedRole === exec.role ? 'var(--color-primary-foreground)' : 'var(--color-primary)'} 
                      />
                    </div>
                    {selectedRole === exec.role && (
                      <Icon name="CheckCircle" size={20} className="text-primary" />
                    )}
                  </div>

                  <h3 className="text-2xl font-bold mb-2 text-text-primary">{exec.role}</h3>
                  <p className="text-sm text-accent mb-4 font-medium">{exec.focus}</p>

                  <div className="space-y-2">
                    {exec.keyQuestions.map((question, idx) => (
                      <div key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
                        <Icon name="ArrowRight" size={14} className="mt-0.5 flex-shrink-0 text-accent" />
                        <span>{question}</span>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Social Proof Banner */}
          <div className="mt-12 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-6 text-text-secondary">
              <div className="flex items-center space-x-2">
                <Icon name="Star" size={16} className="text-yellow-500" />
                <Icon name="Star" size={16} className="text-yellow-500" />
                <Icon name="Star" size={16} className="text-yellow-500" />
                <Icon name="Star" size={16} className="text-yellow-500" />
                <Icon name="Star" size={16} className="text-yellow-500" />
              </div>
              <span className="text-sm font-medium">
                Practitioner team since 2018 | $20M+ client savings (2018–2024 Intelliswift alliance) | Milestone-based guarantees
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExecutiveSummaryHero;
